import React, { useState, useRef, useEffect, useMemo } from 'react';
import { LayoutList, BookOpen, MessageSquare, ExternalLink } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import { getProjectBoard } from '../../../services/miro';
import type { WidgetProps } from '../../../types/second-screen.types';

type FocusTab = 'tasks' | 'transcript' | 'activities' | 'miro';

function isAllowedIframeUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname;
    return hostname === 'miro.com' || hostname.endsWith('.miro.com');
  } catch {
    return false;
  }
}

const TAB_ITEMS: { key: FocusTab; label: string; icon: React.ElementType }[] = [
  { key: 'tasks', label: 'Tasks', icon: LayoutList },
  { key: 'transcript', label: 'Transcript', icon: MessageSquare },
  { key: 'activities', label: 'Activities', icon: BookOpen },
  { key: 'miro', label: 'Miro', icon: ExternalLink },
];

const FocusViewWidget: React.FC<WidgetProps> = ({ config }) => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState<FocusTab>(config?.defaultTab || 'transcript');
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const [autoMiroUrl, setAutoMiroUrl] = useState<string | null>(null);

  // Auto-fetch Miro board URL from project if projectId is in config
  useEffect(() => {
    if (config?.projectId) {
      getProjectBoard(config.projectId)
        .then(result => {
          if (result.board?.boardUrl) {
            setAutoMiroUrl(result.board.boardUrl);
          }
        })
        .catch(() => { /* ignore - fall back to manual config */ });
    }
  }, [config?.projectId]);

  // Resolved Miro URL: auto-fetched from project > manual config
  const miroUrl = autoMiroUrl || config?.miroUrl;

  const currentPhaseIndex = state?.currentPhaseIndex ?? 0;

  // Auto-scroll transcript to bottom
  useEffect(() => {
    if (activeTab === 'transcript' && transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [state?.transcript?.length, activeTab]);

  // Recent transcript entries (last 50)
  const recentTranscript = useMemo(() => {
    const transcript = state?.transcript ?? [];
    return transcript.slice(-50);
  }, [state?.transcript]);

  if (!state) return null;

  const sessionAvatars = state.sessionAvatars ?? [];
  const activeTasks = state.activeTasks ?? [];
  const phases = state.phases ?? [];

  const getAvatarColor = (role?: string) => {
    const avatar = sessionAvatars.find(a => a.role === role);
    return avatar?.primaryColor || '#00c7c4';
  };

  const getAvatarName = (role?: string) => {
    if (!role) return 'Facilitator';
    const avatar = sessionAvatars.find(a => a.role === role);
    return avatar?.name || role;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Tab bar */}
      <div className={`flex shrink-0 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
        {TAB_ITEMS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                isActive
                  ? 'text-primary border-b-2 border-primary'
                  : isDark
                    ? 'text-white/40 hover:text-white/60'
                    : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              <Icon size={12} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {activeTab === 'transcript' && (
          <div className="flex flex-col gap-2">
            {recentTranscript.map((entry, i) => (
              <div key={`${entry.speaker}-${i}`} className="flex gap-2 items-start">
                <span
                  className="text-xs font-bold uppercase shrink-0 w-20 truncate text-right"
                  style={{ color: entry.speaker === 'avatar' ? getAvatarColor(entry.avatarRole) : isDark ? '#94a3b8' : '#64748b' }}
                >
                  {entry.speaker === 'avatar' ? getAvatarName(entry.avatarRole) : 'Facilitator'}
                </span>
                <p className={`text-xs leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
                  {entry.text}
                </p>
              </div>
            ))}
            <div ref={transcriptEndRef} />
          </div>
        )}

        {activeTab === 'tasks' && (
          <div className="flex flex-col gap-2">
            {activeTasks.length === 0 ? (
              <div className={`text-sm text-center py-6 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                No active tasks
              </div>
            ) : (
              activeTasks.map((task, i) => (
                <div
                  key={task.workflowId || i}
                  className={`p-3 rounded-lg border ${
                    isDark ? 'bg-white/[0.03] border-white/5' : 'bg-slate-50 border-slate-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${
                      task.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      task.status === 'running' ? 'bg-primary/20 text-primary' :
                      'bg-white/10 text-white/40'
                    }`}>
                      {task.status}
                    </span>
                    <span className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                      {task.taskName}
                    </span>
                  </div>
                  <span className={`text-xs mt-1 block ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                    {task.taskCategory}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'activities' && (
          <div className="flex flex-col gap-2">
            {phases.map((phase, i) => (
              <div
                key={phase.id}
                className={`p-3 rounded-lg border ${
                  i === currentPhaseIndex
                    ? isDark
                      ? 'bg-primary/10 border-primary/30'
                      : 'bg-primary/5 border-primary/20'
                    : isDark
                      ? 'bg-white/[0.03] border-white/5'
                      : 'bg-slate-50 border-slate-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  {i === currentPhaseIndex && (
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse shrink-0" />
                  )}
                  <span className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                    {phase.name}
                  </span>
                  {phase.duration && (
                    <span className={`text-xs ml-auto ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                      {phase.duration}
                    </span>
                  )}
                </div>
                {phase.objectives && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                    {phase.objectives}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {activeTab === 'miro' && (
          <div className="h-full flex items-center justify-center">
            {miroUrl ? (
              isAllowedIframeUrl(miroUrl) ? (
                <iframe
                  src={miroUrl}
                  title="Miro Board"
                  className="w-full h-full rounded-lg border-0"
                  allow="fullscreen"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                />
              ) : (
                <div className={`text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                  Invalid URL: only miro.com domains are allowed
                </div>
              )
            ) : (
              <div className={`text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                No Miro board connected. Set a project board or configure URL in widget settings.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FocusViewWidget;
